import {
    Dialog,
    DialogContent,
    DialogTitle
} from 'components/ui/dialog';
import { Button } from "../../../components/ui/button"
import { InfoCircle, Refresh, Xmark } from 'iconoir-react';
import { NewProjectModalProps, Project } from "../types/create-project";
import { useEffect, useState } from 'react';
import { Input } from '../../../components/ui/input';
import { useCreateProject } from '../hooks/use-create-project';
import { CreateProjectRequest } from '../services/projects-services';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/use-toast';
import { cn } from 'lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { addProjectToSidebarAndCache } from '../helpers/sync-project-sidebar';
import { DEFAULT_PROJECT_COLOR, ProjectColorSwatches } from './project-color-swatches';

export const NewProjectModal = ({ isOpen, onClose, newConversatioId, callback }: NewProjectModalProps) => {
    const [name, setName] = useState("");
    const [selectedColor, setSelectedColor] = useState<string>(DEFAULT_PROJECT_COLOR);
    const maxLength = 50;

    const { mutateAsync: createProject, isPending } = useCreateProject();
    const { t } = useTranslation();
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const handleCreateProject = async () => {
        if (!name) return;
        const payload: CreateProjectRequest = {
            name: name,
            conversationId: newConversatioId,
            color: selectedColor
        }
        try {
            const response = await createProject({ payload });
            const newProject: Project = {
                id: response.id,
                name: response.name,
                items: [],
                prompts: response.prompts,
                color: response.color ?? selectedColor
            }
            addProjectToSidebarAndCache(queryClient, newProject);
            callback?.();
        } catch (error) {
            const detail =
                (error as { error?: { detail?: string } })?.error?.detail;
            toast({
                variant: "destructive",
                title: t('ERROR') || 'Error',
                description:
                    detail ||
                    t('FAILED_TO_CREATE_PROJECT') ||
                    'Failed to create project.',
            });
        }
        onClose();
        setName("");
        setSelectedColor(DEFAULT_PROJECT_COLOR);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        setName(e.target.value);
    };

    useEffect(() => {
        if (isOpen) {
            setName("");
            setSelectedColor(DEFAULT_PROJECT_COLOR);
        }
    }, [isOpen]);

    return (
        <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent
                hideClose
                overlayClassName="z-[145]"
                className="z-[150] flex max-h-[90vh] w-full max-w-[565px] flex-col gap-6 overflow-y-auto rounded-xl border-0 bg-white p-4 shadow-[0_24px_80px_rgba(15,23,42,0.18)] dark:bg-background sm:p-8"
            >
                <button
                    type="button"
                    aria-label={t('CLOSE') || 'Close'}
                    onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                    }}
                    className="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center text-[#6D6D6D] focus:outline-none dark:text-true-white"
                >
                    <Xmark width={14} height={14} />
                </button>

                <DialogTitle className="self-stretch break-words font-sans text-[24px] font-semibold not-italic leading-[120%] text-grey-700 tracking-normal dark:text-true-white">
                    {t('NEW_PROJECT') || 'New Project'}
                </DialogTitle>

                <div className="flex w-full items-start gap-2 rounded bg-[#FFFAEB] p-4">
                    <div className="flex shrink-0 items-start px-px pb-1 pt-px">
                        <InfoCircle
                            width={13.333}
                            height={13.333}
                            color="#725111"
                            strokeWidth={1.25}
                            className="shrink-0"
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <p className="self-stretch break-words font-sans text-[12px] font-semibold not-italic leading-[120%] text-[#725111]">
                            {t('WHAT_ARE_PROJECTS') || 'What are projects?'}
                        </p>
                        <p className="self-stretch break-words font-sans text-[12px] font-normal not-italic leading-[120%] text-[#725111]">
                            {t('WHAT_ARE_PROJECTS_DESCRIPTION') ||
                                'Projects let you organize chats and add instructions & files to groups of chats.'}
                        </p>
                    </div>
                </div>

                <div className="flex w-full flex-col items-start gap-8 self-stretch pb-4">
                    <div className="flex w-full flex-col items-start gap-1 self-stretch">
                        <label className="font-sans text-sm font-medium not-italic leading-[160%] text-grey-700 dark:text-true-white">
                            {t('NAME') || 'Name'}
                        </label>
                        <Input
                            onClick={(e) => e.stopPropagation()}
                            type="text"
                            placeholder={t('PROJECT_NAME_PLACEHOLDER') || 'e.g. Q1 Marketing Planning'}
                            value={name}
                            onChange={handleChange}
                            maxLength={maxLength}
                            onKeyDown={(e) => e.key === "Enter" && !isPending && name.trim() && handleCreateProject()}
                            className="flex !h-12 w-full items-center gap-4 self-stretch rounded-lg border border-grey-100 bg-white px-5 py-4 font-sans text-sm font-normal not-italic leading-[120%] text-grey-700 shadow-none placeholder:font-normal placeholder:text-grey-300 focus-visible:ring-1 focus-visible:ring-ring dark:border-border dark:bg-transparent dark:text-true-white dark:placeholder:text-grey-400"
                        />
                    </div>

                    <div className="flex w-full flex-col items-start self-stretch">
                        <div className="flex w-full flex-col items-start gap-4 self-stretch">
                            <label className="font-sans text-sm font-bold not-italic leading-[120%] text-grey-700 dark:text-true-white">
                                {t('COLOR') || 'Color'}

                            </label>
                            <ProjectColorSwatches
                                selectedColor={selectedColor}
                                onSelect={setSelectedColor}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-4 self-stretch border-t border-grey-100 pt-6 dark:border-border">
                    <Button
                        variant="outline"
                        className="h-11 w-[101px] gap-1.5 rounded-[62px] border border-[#D9DBE9] px-0 text-base font-medium leading-[115%] text-[#170F49] shadow-none hover:opacity-90"
                        style={{
                            backgroundImage:
                                'linear-gradient(151deg, #FFF -62.65%, #FBFBFE 83.01%)',
                        }}
                        onClick={(e) => { e.stopPropagation(); onClose() }}
                    >
                        {t('CANCEL') || 'Cancel'}
                    </Button>
                    <Button
                        className={cn(
                            'h-11 w-[175px] gap-1.5 rounded-[62px] border-0 px-0 text-base font-semibold leading-[115%] text-white shadow-none',
                            '!bg-black-base hover:!bg-grey-900',
                            'disabled:!bg-grey-200 disabled:!text-grey-500 disabled:!opacity-100',
                            'dark:!bg-[#3D3D3D] dark:hover:!bg-[#4F4F4F] dark:text-true-white',
                            'disabled:dark:!bg-[#27272C] disabled:dark:!text-grey-500 disabled:dark:!opacity-100'
                        )}
                        onClick={(e) => { e.stopPropagation(); handleCreateProject() }}
                        disabled={isPending || !name.trim()}
                    >
                        {t('CREATE_PROJECT') || 'Create Project'}
                        {isPending && (
                            <Refresh className="h-4 w-4 animate-spin" />
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}
